import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import type { OrganizationMember } from '../../types/database'
import { ORG_ROLES, type OrgRole } from '../../types/database'
import {
  VStack,
  HStack,
  PageHeader,
  Text,
  Button,
  Input,
  Badge,
  Table,
  Banner,
  ConfirmDialog,
  Animate,
  Avatar,
  Modal
} from 'wss3-forge'
import {
  Add20Regular,
  Mail20Regular
} from '@fluentui/react-icons'

interface MemberWithEmail extends OrganizationMember {
  email?: string
  avatar_url?: string
}

interface PendingInvite {
  id: string
  invite_email: string
  invite_sent_at: string
  invited_by_email: string | null
}

function getRoleBadgeVariant(role: OrgRole): 'primary' | 'success' | 'warning' | 'default' {
  switch (role) {
    case 'owner': return 'primary'
    case 'admin': return 'success'
    case 'sso_admin': return 'warning'
    default: return 'default'
  }
}

export function OrgMembers() {
  const navigate = useNavigate()
  const { organization } = useOrg()

  const [members, setMembers] = useState<MemberWithEmail[]>([])
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [showCancelInvite, setShowCancelInvite] = useState(false)
  const [selectedInvite, setSelectedInvite] = useState<PendingInvite | null>(null)

  useEffect(() => {
    loadData()
  }, [organization.id])

  const loadData = async () => {
    try {
      const { data: membersData, error: membersError } = await supabase
        .from('organization_members')
        .select('*')
        .eq('organization_id', organization.id)
        .not('invite_accepted_at', 'is', null)
        .order('created_at', { ascending: false })

      if (membersError) throw membersError

      // Get all user_ids to fetch in batch
      const userIds = (membersData || [])
        .filter(m => m.user_id)
        .map(m => m.user_id)

      // Fetch all user data in a single batch call
      let userDataMap: Record<string, { email: string; avatar_url: string | null }> = {}
      if (userIds.length > 0) {
        const { data: usersData } = await supabase.rpc('get_users_batch', {
          p_user_ids: userIds
        })
        if (usersData) {
          userDataMap = (usersData as { user_id: string; email: string; avatar_url: string | null }[]).reduce((acc, u) => {
            acc[u.user_id] = { email: u.email, avatar_url: u.avatar_url }
            return acc
          }, {} as Record<string, { email: string; avatar_url: string | null }>)
        }
      }

      // Map members with their user data
      const membersWithAvatars = (membersData || []).map(m => {
        if (m.user_id && userDataMap[m.user_id]) {
          return {
            ...m,
            email: userDataMap[m.user_id].email || m.invite_email,
            avatar_url: userDataMap[m.user_id].avatar_url || undefined
          }
        }
        return { ...m, email: m.invite_email }
      })

      setMembers(membersWithAvatars)

      const { data: invitesData, error: invitesError } = await supabase.rpc('get_org_pending_invites', {
        p_organization_id: organization.id
      })

      if (invitesError) throw invitesError
      setPendingInvites(invitesData || [])

    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(inviteEmail.trim())) {
      setError('Invalid email address')
      return
    }

    setInviting(true)
    setError(null)

    try {
      const { error } = await supabase.rpc('create_org_admin_invite', {
        p_organization_id: organization.id,
        p_email: inviteEmail.trim()
      })

      if (error) throw error

      setSuccess(`Invitation sent to ${inviteEmail}`)
      setInviteEmail('')
      setShowInviteForm(false)
      loadData()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setInviting(false)
    }
  }

  const handleCancelInvite = async () => {
    if (!selectedInvite) return

    try {
      const { error } = await supabase.rpc('cancel_org_invite', {
        p_member_id: selectedInvite.id
      })

      if (error) throw error

      setPendingInvites(prev => prev.filter(i => i.id !== selectedInvite.id))
      setSuccess('Invitation cancelled')
    } catch (err: any) {
      setError(err.message)
    }
    setShowCancelInvite(false)
    setSelectedInvite(null)
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <PageHeader
          title="Members"
          subtitle="Manage administrators and members of your organization."
        />
      </Animate>

      {error && (
        <Banner variant="error" title="Error" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {success && (
        <Banner variant="success" title="Success" onDismiss={() => setSuccess(null)}>
          {success}
        </Banner>
      )}

      <Modal
        open={showInviteForm}
        onClose={() => setShowInviteForm(false)}
        title="Add User"
      >
        <VStack gap="md">
          <Text color="muted">
            The invitee will receive an email with a link to join the organization.
          </Text>

          <Input
            placeholder="email@company.com"
            type="email"
            value={inviteEmail}
            onChange={(v) => setInviteEmail(v as string)}
            icon={<Mail20Regular />}
          />

          <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <Button variant="secondary" onClick={() => setShowInviteForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} loading={inviting}>
              Send Invitation
            </Button>
          </HStack>
        </VStack>
      </Modal>

      {pendingInvites.length > 0 && (
        <Animate type="slideInUp" delay={50}>
          <Table
            title="Pending Invitations"
            columns={[
              {
                key: 'email',
                header: 'Email',
                render: (_, row: PendingInvite) => (
                  <Text>{row.invite_email}</Text>
                )
              },
              {
                key: 'sent',
                header: 'Sent on',
                render: (_, row: PendingInvite) => (
                  <Text size="sm" color="muted">
                    {new Date(row.invite_sent_at).toLocaleDateString('en-US')}
                  </Text>
                )
              },
              {
                key: 'actions',
                header: '',
                render: (_, row: PendingInvite) => (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedInvite(row)
                      setShowCancelInvite(true)
                    }}
                  >
                    Cancel
                  </Button>
                )
              }
            ]}
            data={pendingInvites}
          />
        </Animate>
      )}

      <Animate type="slideInUp" delay={100}>
        <Table
          title="Active Members"
          columns={[
            {
              key: 'email',
              header: 'Email',
              render: (_, row: MemberWithEmail) => (
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <Avatar name={row.email || ''} src={row.avatar_url} size="sm" />
                  <Text weight="medium">{row.email || row.user_id}</Text>
                </HStack>
              )
            },
            {
              key: 'role',
              header: 'Role',
              width: '140px',
              render: (_, row: MemberWithEmail) => (
                <Badge variant={getRoleBadgeVariant(row.role)}>
                  {ORG_ROLES.find(r => r.id === row.role)?.label || row.role}
                </Badge>
              )
            },
            {
              key: 'source',
              header: 'Source',
              render: (_, row: MemberWithEmail) => (
                <Badge size="sm">
                  {row.created_via === 'sso' ? 'SSO' : row.created_via === 'invite' ? 'Invitation' : 'Manual'}
                </Badge>
              )
            },
            {
              key: 'last_login',
              header: 'Last Login',
              render: (_, row: MemberWithEmail) => (
                <Text size="sm" color="muted">
                  {row.last_sso_login_at
                    ? new Date(row.last_sso_login_at).toLocaleDateString('en-US')
                    : '-'}
                </Text>
              )
            }
          ]}
          data={members}
          loading={loading}
          onRowClick={(row) => navigate(`/org/${organization.slug}/members/${row.id}`)}
          globalActions={
            <Button
              size="sm"
              icon={<Add20Regular />}
              onClick={() => setShowInviteForm(true)}
            >
              Add User
            </Button>
          }
        />
      </Animate>

      <ConfirmDialog
        open={showCancelInvite}
        onClose={() => setShowCancelInvite(false)}
        title="Cancel Invitation"
        description={`Cancel the invitation sent to ${selectedInvite?.invite_email}?`}
        confirmText="Cancel Invitation"
        cancelText="Close"
        variant="danger"
        onConfirm={handleCancelInvite}
      />
    </VStack>
  )
}
